/**
 * Utility functions for generating language-aware URLs
 */

export type Language = 'en' | 'fr';

/**
 * Get the base URL based on environment
 */
export function getBaseUrl(): string {
  if (typeof window !== 'undefined') {
    // Client-side: use current origin
    return window.location.origin;
  }

  // Server-side: use environment variable or fallback
  return process.env.PUBLIC_URL || 'https://pierreft.com';
}

/**
 * Generate a language-aware URL
 */
export function createLanguageAwareUrl(
  path: string,
  language: Language = 'en',
  searchParams?: Record<string, string>
): string {
  const baseUrl = getBaseUrl();
  const prefix = language === 'fr' ? '/fr' : '';

  // Remove leading slash from path if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  let url = `${baseUrl}${prefix}/${cleanPath}`;

  // Add search parameters if provided
  if (searchParams && Object.keys(searchParams).length > 0) {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      params.append(key, value);
    });
    url += `?${params.toString()}`;
  }

  return url;
}

/**
 * Generate success URL for Stripe checkout with language support
 */
export function createSuccessUrl(language: Language = 'en'): string {
  return createLanguageAwareUrl('dashboard', language, {
    purchase_success: 'true'
  });
}

/**
 * Generate cancel URL for Stripe checkout with language support
 */
export function createCancelUrl(language: Language = 'en'): string {
  return createLanguageAwareUrl('courses', language);
}

/**
 * Get current language from URL path or localStorage
 */
export function getCurrentLanguage(): Language {
  if (typeof window === 'undefined') {
    return 'en';
  }

  // Check URL path first
  const path = window.location.pathname;
  if (path.startsWith('/fr/')) {
    return 'fr';
  }

  // Check localStorage preference
  try {
    const storedPreference = localStorage.getItem('language_preference');
    if (storedPreference) {
      const preference = JSON.parse(storedPreference);
      if (preference?.language === 'fr' || preference?.language === 'en') {
        return preference.language;
      }
    }

    // Fallback to old format
    const storedLang = localStorage.getItem('lang');
    if (storedLang === 'fr' || storedLang === 'en') {
      return storedLang;
    }
  } catch (error) {
    console.error('Error reading language preference:', error);
  }

  // Default to English
  return 'en';
}